/* eslint-disable max-len */
import React from "react";
import styles from "./services.module.scss";
import { TechCard } from "../../components/tech-card/TechCard.component";
import Architecture from "@assets/images/architecture.jpg";
import Cloud from "@assets/images/cloud.jpg";
import Integrate from "@assets/images/integrate.jpg";
import AI from "@assets/images/ai.jpg";
import Blockchain from "@assets/images/blockchain.jpg";
import {
  spaceshipIcon,
  separator05,
  separator06,
  separator07,
  separator08,
  separator09,
} from "../../utils/svg";
import {
  ThreeIcon,
  IconType,
} from "../../components/three-icon/ThreeIcon.component";
import { ScrollReveal } from "../../components/scroll-reveal/ScrollReveal.component";
import { LazyImage } from "../../components/lazy-image/LazyImage.component";
import { SeoComponent } from "../../components/seo/Seo.component";

const development: React.FC = () => {
  return (
    <>
      <p>
        We build robust, scalable, and high-performance applications tailored to
        your specific needs. Our expertise spans the entire stack, from
        responsive front-end interfaces to complex back-end systems.
      </p>
      <div className={styles["separator-container"]}>
        <svg viewBox="0 0 384 24" className={styles["separator"]}>
          <path d={separator05} />
        </svg>
      </div>
      <p>
        Whether you need a traditional Web2 application or a decentralized Web3
        solution, our team leverages the latest technologies and best practices
        to deliver software that stands the test of time. We prioritize clean
        code, maintainability, and user-centric design.
      </p>
    </>
  );
};

const advisory: React.FC = () => {
  return (
    <>
      <p>
        Navigating the startup landscape can be challenging. We offer strategic
        advisory services to help early-stage companies define their
        product-market fit, optimize their technical roadmap, and scale
        effectively.
      </p>
      <div className={styles["separator-container"]}>
        <svg viewBox="0 0 384 24" className={styles["separator"]}>
          <path d={separator06} />
        </svg>
      </div>
      <p>
        Our experience with successful startups allows us to provide actionable
        insights on everything from team building and fundraising to technology
        selection and go-to-market strategies. We act as your technical partner,
        ensuring your business is built on a solid foundation.
      </p>
    </>
  );
};

const security: React.FC = () => {
  return (
    <>
      <p>
        Security is paramount in today's digital world. We provide comprehensive
        security assessments and audit liaising services to ensure your
        applications and infrastructure are protected against threats.
      </p>
      <div className={styles["separator-container"]}>
        <svg viewBox="0 0 384 24" className={styles["separator"]}>
          <path d={separator07} />
        </svg>
      </div>
      <p>
        We work closely with top-tier audit firms to facilitate smart contract
        audits and security reviews. Our team helps you prepare for audits,
        remediate vulnerabilities, and implement best practices to safeguard
        your assets and user data.
      </p>
    </>
  );
};

const aiSolutions: React.FC = () => {
  return (
    <>
      <p>
        Artificial Intelligence is transforming industries. We help you harness
        the power of AI to automate processes, gain insights from data, and
        create intelligent user experiences.
      </p>
      <div className={styles["separator-container"]}>
        <svg viewBox="0 0 384 24" className={styles["separator"]}>
          <path d={separator08} />
        </svg>
      </div>
      <p>
        From integrating Large Language Models (LLMs) to developing custom
        machine learning models, we guide you through the complexities of AI
        adoption. We focus on ethical AI development and practical applications
        that deliver real business value.
      </p>
    </>
  );
};

const blockchainSolutions: React.FC = () => {
  return (
    <>
      <p>
        Blockchain technology offers new possibilities for transparency,
        security, and decentralized governance. We are experts in building
        decentralized applications (dApps), smart contracts, and token
        economies.
      </p>
      <div className={styles["separator-container"]}>
        <svg viewBox="0 0 384 24" className={styles["separator"]}>
          <path d={separator09} />
        </svg>
      </div>
      <p>
        Our deep understanding of various blockchain ecosystems, including
        Ethereum and Layer 2 solutions, enables us to architect and build secure
        and efficient blockchain solutions. Whether you're launching a DeFi
        protocol, an NFT marketplace, or a DAO, we have the expertise to support
        you.
      </p>
    </>
  );
};

const items: {
  image: string;
  title: string;
  text: React.FC;
  iconType: IconType;
}[] = [
  {
    image: Architecture,
    title: "Full-Stack Development",
    text: development,
    iconType: "full-stack",
  },
  {
    image: Cloud,
    title: "Startup Advisory",
    text: advisory,
    iconType: "sphere",
  },
  {
    image: Integrate,
    title: "Security & Audits",
    text: security,
    iconType: "shield",
  },
  { image: AI, title: "AI Solutions", text: aiSolutions, iconType: "torus" },
  {
    image: Blockchain,
    title: "Blockchain Solutions",
    text: blockchainSolutions,
    iconType: "ethereum",
  },
];

export const Services: React.FC = () => {
  return (
    <>
      <SeoComponent
        title="Services | Volumetric"
        description="Explore our core services in software development, including React, TypeScript, Node.js, and 3D web experiences."
      />
      <div className={styles["container"]}>
        <div className={styles["services"]}>
          <ScrollReveal animation="fade-up">
            <TechCard
              title="Our Services"
              icon={spaceshipIcon}
              iconViewBox="0 0 543 543">
              <ServicesIntro />
            </TechCard>
          </ScrollReveal>

          <div className={styles["services-list"]}>
            {items.map((item, index) => (
              <ScrollReveal
                key={index}
                animation="fade-up"
                delay={index * 100} // Stagger effect
              >
                <TechCard
                  title={item.title}
                  iconElement={<ThreeIcon type={item.iconType} />}>
                  <div
                    id={`service-${index}`}
                    className={styles["service-content"]}>
                    <div className={styles["image-wrapper"]}>
                      <LazyImage src={item.image} alt={item.title} />
                    </div>
                    <div className={styles["text-wrapper"]}>
                      <item.text />
                    </div>
                  </div>
                </TechCard>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </div>
    </>
  );
};

const ServicesIntro: React.FC = () => {
  const scrollToService = (index: number) => {
    const element = document.getElementById(`service-${index}`);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <div className={styles["column"]}>
      <p>
        Volumetric Labs offers a comprehensive suite of services designed to
        help businesses thrive in the digital age. From initial concept to final
        deployment, we provide the expertise and support needed to turn
        ambitious ideas into reality.
      </p>
      <div className={styles["services-menu"]}>
        {items.map((item, index) => (
          <div
            key={index}
            className={styles["menu-item"]}
            onClick={() => scrollToService(index)}>
            {item.title}
          </div>
        ))}
      </div>
    </div>
  );
};
